// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general components..
import Skeleton from './components/Skeleton.vue';

// Import general functions for shared functionality.
import constants from '../constants.js';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

Swiper.use([Pagination, Navigation]);

// Import axios for API consuming in a clean interface.
import axios from 'axios';

const defaultApp = {
    mixins: [mixin],
    components: { Skeleton },
    data() {
        return {
            dataApi: '',
            dataLimit: null,
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataWidget: false,
            dataOverruleIds: [],
            dataOverruleCountries: [],
            dataOverruleExpertises: [],
            dataOverruleServices: [],
            dataCulture: 'nl-NL',
            loading: false,
            loadingMore: false,
            debounceSetLoading: null,
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            page: 1,
            pageLimit: 100,
            searchText: '',
            filterParams: null,
            filterDate: '',
            language: 'en',
            delimiter: '-',

            constants: constants,
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets.length;
        },
        itemsSet() {
            return this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet && this.data.facets.some(facet => facet.values.length)) || this.dataOverruleIds.length;
        },
        filtersActive() {
            return (this.facetsSet && this.data.facets.some(facet => facet.selected)) || this.searchText;
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection || this.searchText) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            if (this.filterParams) {
                return Array.from(this.filterParams.entries()).map(([key, value]) => ({
                    Key: key,
                    Values: value.split(','),
                    Operator: constants.operators.in,
                }));
            }
            let excludeFilter = [];
            if (this.dataOverruleIds.length) {
                if (!this.itemsSet) {
                    return [
                        {
                            Key: this.constants.keys.id,
                            Values: this.dataOverruleIds,
                            Operator: this.constants.operators.in,
                        }
                    ]
                }
            }

            if (this.itemsSet && this.dataWidget) {
                excludeFilter.push({
                    Key: this.constants.keys.id,
                    Values: this.data.items.map(item => item.id),
                    Operator: this.constants.operators.notIn,
                });
            }
            
            let filters = this.data.facets.map(facet => {
                if (facet.values.length) {
                    return {
                        Key: facet.alias,
                        Values: facet.values,
                        Operator: this.constants.operators.in,
                    }
                }
            }).filter(item => item);

            if (this.dataOverruleCountries.length) {
                filters.push({
                    Key: this.constants.keys.country,
                    Values: this.dataOverruleCountries,
                    Operator: this.constants.operators.in,
                });
            }

            if (this.dataOverruleExpertises.length) {
                filters.push({
                    Key: this.constants.keys.expertises,
                    Values: this.dataOverruleExpertises,
                    Operator: this.constants.operators.in,
                });
            }

            if (this.dataOverruleServices.length) {
                filters.push({
                    Key: this.constants.keys.services,
                    Values: this.dataOverruleServices,
                    Operator: this.constants.operators.in,
                });
            }

            return [...filters, ...excludeFilter];
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.itemsSet && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText
            }
        },
        amountsSentence() {
            if (!this.data || !this.data.queryCount) return null;
            return this.dataAmountsSentence
                .replace(this.constants.magicStrings.queryCount, this.data.items.length)
                .replace(this.constants.magicStrings.totalCount, this.data.queryCount);
        },
    },
    methods: {
        async init() {
            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget;
            if (this.$el.dataset.overruleIds) this.dataOverruleIds = this.$el.dataset.overruleIds.split(',');
            if (this.$el.dataset.overruleCountries) this.dataOverruleCountries = this.$el.dataset.overruleCountries.split(',');
            if (this.$el.dataset.overruleExpertises) this.dataOverruleExpertises = this.$el.dataset.overruleExpertises.split(',');
            if (this.$el.dataset.overruleServices) this.dataOverruleServices = this.$el.dataset.overruleServices.split(',');
            if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.dataCulture;
            if (this.$el.dataset.limit && !this.dataLimit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortKey) this.dataSortKey = this.$el.dataset.sortKey;
            if (this.$el.dataset.sortDirection) this.dataSortDirection = this.$el.dataset.sortDirection;
            if (this.$el.dataset.amountsSentence) this.dataAmountsSentence = this.$el.dataset.amountsSentence;
            this.delimiter = this.dataCulture === this.constants.culture.dutch ? '-' : '/';
            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam > this.pageLimit) pageParam = this.pageLimit;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            this.filterParams = urlParams.toString() ? urlParams : null;
            if (this.filterParams) {
                this.filterDate = this.filterParams.get(this.constants.keys.date) ? this.filterParams.get(this.constants.keys.date) : '';
            }

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS.
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataLimit = this.dataLimit - this.dataOverruleIds.length;
            }

            if (this.itemsSet < this.dataLimit + this.dataOverruleIds.length) {
                let tempLimit = this.dataLimit;
                if (pageParam && !this.dataWidget) {
                    this.page = pageParam;
                    this.dataLimit = this.dataLimit = 9999 ? 9999 : this.dataLimit * this.page;
                }

                // Initial data fetch.
                const filteredResponse = await this.fetchData();
                let replace = !this.itemsSet;
                this.setData(filteredResponse, replace);
                this.dataLimit = tempLimit;
                this.filterParams = null;

                // If there are still not enough items, fetch more data without filters.
                if (this.itemsSet < this.dataLimit + this.dataOverruleIds.length && this.dataWidget) {
                    this.dataLimit = this.dataLimit + this.dataOverruleIds.length - this.itemsSet;
                    this.dataOverruleExpertises = [];
                    this.dataOverruleCountries = [];
                    this.dataOverruleServices = [],
                    this.data.facets = [];
                    const response = await this.fetchData();
                    let replace = !this.itemsSet;
                    this.setData(response, replace);
                }
            }
            
            if (this.itemsSet) {
                this.$nextTick(() => {
                    if (this.$refs.slider) {
                        let $slider = this.$refs.slider;
                        let next = $slider.parentNode.querySelector('.swiper-button-next');
                        let prev = $slider.parentNode.querySelector('.swiper-button-prev');
                        let pagination = $slider.parentNode.querySelector('.swiper-pagination');
                        new Swiper($slider, {
                            spaceBetween: 24,
                            slidesPerView: 1.2,
                            breakpoints: {
                                768: {
                                    slidesPerView: 2.7
                                },
                                1024: {
                                    slidesPerView: 3.1
                                },
                                1407: {
                                    slidesPerView: 3.5
                                },
                            },
                            navigation: {
                                nextEl: next,
                                prevEl: prev,
                            },
                            pagination: {
                                el: pagination,
                                clickable: true,
                                type: 'fraction'
                            }
                        });
                    }
                });
            }
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        async fetchData(loading = true) {
            if (loading) this.debounceSetLoading(true);
            let response = null;
            try {
                response = await axios.post(this.dataApi, this.queryModel);
            } catch (error) {
                console.log(error);
            } finally {
                if (loading) this.debounceSetLoading(false);
            }
            return response;
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData() {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore() {
            this.loadingMore = true;
            this.page++;
            const response = await this.fetchData(false);
            this.setData(response);
            this.loadingMore = false;
            this.updateUrl();
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            }
            else {
                response.data.items.forEach(item => {
                    this.data.items.push(item);
                })
            }
            this.setFacets(response);
        },
        // The code below is needed when facets have different operators.
        // Method to set facet values based on URL search params.
        //setFacetValuesFromUrl(filterParams) {
        //    if (!this.facetsSet) return;
        //    this.data.facets.forEach(facet => {
        //        if (filterParams.has(facet.alias)) {
        //            let valueArray = filterParams.get(facet.alias).split(',');
        //            facet.values = valueArray
        //                .map(alias => facet.options.find(option => option.valueAlias === alias))
        //                .map(option => option.valueAlias);
        //        }
        //    });
        //},
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(facet => ({
                ...facet,
                values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
            }));
            this.data.queryCount = response.data.queryCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        selectOption(facetAlias, event) {
            let optionAlias = event.target.value;
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (optionAlias === '') {
                changedFacet.values = [];
            } else {
                changedFacet.values = [optionAlias];
            }
            this.fetchInitialData();
        },
        // Data alteration function for reloading data with set filters.
        toggleOption(facetAlias, optionAlias) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (changedFacet && changedFacet.values.includes(optionAlias)) {
                changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
            } else {
                changedFacet.values.push(optionAlias);
            }
            this.fetchInitialData();
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach(filterItem => urlParams.append(filterItem.Key, filterItem.Values.join()))
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index, date) {
            if (!this.orderItems.length) return null;
            let returnVal = null;
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat(this.dataCulture, { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
            }
            else {
                let prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.date).getMonth() || this.toDateObj(date).getYear() !== this.toDateObj(prevEvent.date).getYear()) {
                        returnVal = new Intl.DateTimeFormat(this.dataCulture, { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                }
            }
            return returnVal;
        },
        createDebouncedLoading() {
            return this.debounce((state) => {
                this.loading = state;
            }, 500);
        },
    },
    created() {
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
        // Initialize the debounced loading function.
        this.debounceSetLoading = this.createDebouncedLoading();
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    },
};

export default defaultApp;
